<template>
  <SfDropdown
    dropdown-id="add-filter-search"
    :is-open="isOpen"
    :is-full-width="true"
    :unstyled-trigger="true"
    @dropdown-closed="toggleDropdown(false)"
  >
    <template #trigger>
      <div @click="focusInput">
        <SfSearchInput
          id="filters-search"
          v-model="searchValue"
          :placeholder="I18n.t('companies_overview.filters.search_placeholder')"
          :focused-at="focusedAt"
          :show-result-navigation="false"
          @focus="onInputFocus"
          @clear="handleClearSearch"
          @keydown.down.prevent="moveHighlight(1)"
          @keydown.up.prevent="moveHighlight(-1)"
          @keydown.enter.prevent="selectHighlighted"
        />
      </div>
    </template>

    <template #menu>
      <div
        ref="menuRef"
        class="tw-max-h-80 tw-overflow-y-auto"
        @mousemove="isKeyboardNavigating = false"
      >
        <SfDropdownItem
          v-if="filteredItems.length === 0"
          :has-border="false"
          :is-disabled="true"
        >
          {{ I18n.t('companies_overview.filters.search_empty') }}
        </SfDropdownItem>
        <SfDropdownItem
          v-for="(item, index) in filteredItems"
          v-else
          :key="`${item.type}-${item.name}`"
          :has-border="false"
          :is-wrapped="true"
          :is-disabled="!!item.disabledOnFirmLevel?.()"
          :class="itemClasses(index)"
          @mouseenter="onItemMouseEnter(index)"
          @click="editFilter(item)"
        >
          <div>
            <div class="tw-flex tw-items-center tw-gap-1">
              <span>
                <span class="tw-font-medium">{{
                  filterTitle(item.nameKey)
                }}</span
                ><span v-if="item.attributeLabel"
                  >: {{ item.attributeLabel }}</span
                >
              </span>
              <i
                v-if="liteMode"
                class="fa fa-gem tw-text-xs tw-text-sf-primary-dark"
              />
            </div>
            <div v-if="item.description" class="tw-text-sf-neutral tw-text-xs">
              {{ item.description }}
            </div>
          </div>
        </SfDropdownItem>
      </div>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfSearchInput } from '@app/shared/vue/components/sf-search'
import {
  SfDropdown,
  SfDropdownItem,
} from '@app/shared/vue/components/sf-dropdown'
import { useSearchable } from '@app/shared/vue/composables/use-searchable'
import { filterDefinitions } from '@app/insights/filter-definitions'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import { useFiltersStore } from '@app/insights/store/filters'
import { filterTitle, filterModalHelp } from '@app/insights/utils/filter-texts'
import type {
  FilterDefinition,
  SearchableFilterDefinition,
} from '@app/insights/types'
import { useFilterVisibility } from '@app/insights/composables/use-filter-visibility'

defineOptions({ name: 'AddFilterSearch' })

const props =
  defineProps<{
    liteMode?: boolean
  }>()

const emit =
  defineEmits<{
    editFilter: []
  }>()

// Composables
const I18n = useI18n()
const rootStore = useInsightsRootStore()
const filtersStore = useFiltersStore()

// State
const isOpen = ref(false)
const focusedAt = ref(0)
const highlightedIndex = ref(-1)
const menuRef = ref<HTMLElement | null>(null)
const isKeyboardNavigating = ref(false)

onMounted(() => {
  focusedAt.value = Date.now()
})

const { isFilterVisible } = useFilterVisibility()

const items = computed<SearchableFilterDefinition[]>(() =>
  (filterDefinitions as FilterDefinition[])
    .filter(isFilterVisible)
    .flatMap((definition) => {
      const title = filterTitle(definition.nameKey)
      if (definition.searchableAttributes) {
        return definition.searchableAttributes().map(({ settings, label }) => ({
          ...definition,
          name: `${title}: ${label}`,
          description: '',
          attributeSettings: settings,
          attributeLabel: label,
        }))
      }
      return [
        {
          ...definition,
          name: title,
          description: filterModalHelp(definition.nameKey),
        },
      ]
    })
)

const { searchValue, results: nameResults } = useSearchable<
  SearchableFilterDefinition,
  SearchableFilterDefinition
>(items, {
  prepareItem: (item) => item,
  fuzzySortOptions: { keys: ['name', 'searchKeywords'] },
})

// Computed
const filteredItems = computed(() => {
  const query = searchValue.value.toLowerCase().trim()

  if (!query)
    return [...items.value].sort((a, b) => a.name.localeCompare(b.name))

  const nameMatched = new Set(nameResults.value)
  const descriptionMatched = items.value.filter(
    (item) =>
      !nameMatched.has(item) &&
      item.description
        .toLowerCase()
        .split(/\s+/)
        .some((word) => word.startsWith(query))
  )

  return [...nameResults.value, ...descriptionMatched].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
})

watch(searchValue, (value) => {
  isOpen.value = !!value
})

watch(filteredItems, (items) => {
  highlightedIndex.value = items.length === 1 ? 0 : -1
})

function itemClasses(index: number) {
  return [
    isKeyboardNavigating.value
      ? 'tw-pointer-events-none'
      : 'hover:tw-bg-sf-primary-subtle',
    index === highlightedIndex.value ? 'tw-bg-sf-primary-subtle' : '',
  ]
}

// Handlers
function handleClearSearch() {
  searchValue.value = ''
}

function toggleDropdown(value: boolean) {
  isOpen.value = value
}

function onInputFocus() {
  isOpen.value = !!searchValue.value
}

function onItemMouseEnter(index: number) {
  isKeyboardNavigating.value = false
  highlightedIndex.value = index
}

function moveHighlight(direction: 1 | -1) {
  isKeyboardNavigating.value = true
  if (!isOpen.value) {
    isOpen.value = true
    return
  }

  const count = filteredItems.value.length
  if (count === 0) return

  highlightedIndex.value = (highlightedIndex.value + direction + count) % count
  nextTick(() => {
    const items = menuRef.value?.querySelectorAll<HTMLElement>(
      '[data-testid="dropdown-item"]'
    )
    items?.[highlightedIndex.value]?.scrollIntoView({ block: 'nearest' })
  })
}

function selectHighlighted() {
  if (
    highlightedIndex.value >= 0 &&
    highlightedIndex.value < filteredItems.value.length
  ) {
    const item = filteredItems.value[highlightedIndex.value]
    if (!item.disabledOnFirmLevel?.()) editFilter(item)
  }
}

function focusInput() {
  focusedAt.value = Date.now()
}

function editFilter(filter: SearchableFilterDefinition) {
  toggleDropdown(false)

  if (props.liteMode) {
    rootStore.togglePremiumModal()
  } else {
    const filterToEdit = filter.attributeSettings
      ? { ...filter, settings: filter.attributeSettings }
      : filter

    filtersStore.editFilter(filterToEdit)
  }

  emit('editFilter')
}
</script>

<style scoped>
:deep(.sf-dropdown2-select),
:deep(.sf-dropdown2-select:hover),
:deep(.sf-dropdown2-select:active),
:deep(.sf-dropdown2-select:focus) {
  border: none !important;
  box-shadow: none !important;
}

:deep(.sf-dropdown2-menu) {
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 10%), 0 4px 6px -4px rgb(0 0 0 / 10%);
  border-radius: 0.5rem;
}
</style>
